import React, { useEffect, useState } from 'react';
import { Link as DomLink } from 'react-router-dom';

import User from 'Services/User';
import ModalService from 'Services/Modal';
import MeApi from 'Services/Api/Me/Me';
import { currentRegisteredStage } from 'Services/AttendanceHelpers';

import Confirm from 'Components/Partials/Modals/Confirm';
import CancelAttendanceModal from 'Components/Partials/Modals/CancelAttendanceModal';

/**
 * The registration status of whoever is logged in, kept up to date as they act.
 *
 * Cancelling refreshes the user, so follow that rather than tracking results locally —
 * every call to action on the page then agrees about where the delegate stands.
 *
 * @return {string|null}
 */
const useRegisteredStage = () => {
    const [stage, setStage] = useState(currentRegisteredStage);

    useEffect(() => {
        const handleChange = () => setStage(currentRegisteredStage());

        User.on('change', handleChange);

        return () => User.removeListener('change', handleChange);
    }, []);

    return stage;
};

const openCancelAttendanceModal = () => ModalService.open({
    component: CancelAttendanceModal,
});

const openLeaveWaitlistModal = () => ModalService.open({
    component: Confirm,
    props: {
        title: 'Leave the in-person waitlist?',
        message: 'You will keep your virtual place, and we will stop contacting you about in-person spaces.',
        cancelText: 'Stay on the waitlist',
        confirmText: 'Leave the waitlist',
        onConfirm: () => MeApi.cancelAttendance({ action: 'switch_to_virtual' }),
    },
});

/**
 * The call to action that matches the delegate's registration status.
 *
 * Someone who already holds a place is not being asked to register again: they get the
 * action for giving it up instead, and a virtual delegate — who has nothing to give up —
 * gets no button at all.
 *
 * @param {string} className    Applied to the link or button so each caller keeps its styling.
 * @param {string} registerTo   Where the register link points.
 * @param {string} registerText Label for the register link.
 */
const AttendanceActions = ({
    className,
    registerTo = '/registration',
    registerText = 'Register Now',
}) => {
    const stage = useRegisteredStage();

    if (stage === 'confirm-attending') {
        return (
            <button type="button" onClick={ openCancelAttendanceModal } className={ className }>
                Cancel my place
            </button>
        );
    }

    if (stage === 'confirm-waitlist') {
        return (
            <button type="button" onClick={ openLeaveWaitlistModal } className={ className }>
                Leave the waitlist
            </button>
        );
    }

    if (stage === 'confirm-not-attending') {
        return null;
    }

    return (
        <DomLink to={ registerTo } className={ className }>{ registerText }</DomLink>
    );
};

export default AttendanceActions;

export {
    useRegisteredStage,
    openCancelAttendanceModal,
    openLeaveWaitlistModal,
};
